import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TooltipProvider } from '@/components/ui/tooltip';
import LevelProgressCard from '@/components/gamification/LevelProgressCard';
import BadgeTile from '@/components/gamification/BadgeTile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  es: { lessons: 'Lecciones', tasks: 'Tareas', corrections: 'Correcciones', reinforcement: 'Refuerzo Academico' },
  en: { lessons: 'Lessons', tasks: 'Tasks', corrections: 'Corrections', reinforcement: 'Academic Reinforcement' },
};
const CATEGORY_ORDER = ['lessons', 'tasks', 'corrections', 'reinforcement'];

function groupByCategory(list) {
  const out = {};
  for (const b of list) {
    if (!out[b.category]) out[b.category] = [];
    out[b.category].push(b);
  }
  return out;
}

function orderedCategories(earnedGrouped, lockedGrouped) {
  const allCats = [...new Set([...Object.keys(earnedGrouped), ...Object.keys(lockedGrouped)])];
  return [
    ...CATEGORY_ORDER.filter(c => allCats.includes(c)),
    ...allCats.filter(c => !CATEGORY_ORDER.includes(c)),
  ];
}

export default function GamificationPanel({ studentId, badgesOnly = false }) {
  const { token } = useAuth();
  const { language, t } = useLanguage();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gamification/profile/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('[GamificationPanel] fetch error:', err);
    }
  }, [studentId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!data) return null;

  const { xp, level, levels, badges } = data;
  const badgeList = badges || [];
  const earnedGrouped = groupByCategory(badgeList.filter(b => b.earned));
  const lockedGrouped = groupByCategory(badgeList.filter(b => !b.earned));
  const allCategories = orderedCategories(earnedGrouped, lockedGrouped);
  const hasAnyEarned = badgeList.some(b => b.earned);

  return (
    <div className="space-y-6" data-testid="gamification-panel">
      {!badgesOnly && (
        <LevelProgressCard
          xp={xp}
          level={level}
          levels={levels}
          language={language}
          tProgress={t('my_progress')}
          tXpPoints={t('xp_points')}
          tXpToNext={t('xp_to_next')}
        />
      )}

      <Card className="rounded-xl">
        <CardHeader className="pb-3">
          <CardTitle style={{ fontFamily: 'Montserrat' }}>{t('badges')}</CardTitle>
        </CardHeader>
        <CardContent>
          {!hasAnyEarned && badgeList.length > 0 && (
            <p className="text-sm text-slate-400 mb-4">{t('no_badges_yet')} - {t('keep_going')}</p>
          )}

          <TooltipProvider delayDuration={200}>
            {allCategories.map(cat => {
              const catLabel = CATEGORY_LABELS[language]?.[cat] || cat;
              const all = [...(earnedGrouped[cat] || []), ...(lockedGrouped[cat] || [])];
              return (
                <div key={cat} className="mb-5 last:mb-0">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{catLabel}</h4>
                  <div className="flex flex-wrap gap-3">
                    {all.map(b => (
                      <BadgeTile key={b.id} badge={b} language={language} fallbackHint={t('keep_going')} />
                    ))}
                  </div>
                </div>
              );
            })}
          </TooltipProvider>
        </CardContent>
      </Card>
    </div>
  );
}
